import type { Payment } from "@/payments/types/payments";
import type { Reader } from "@/terminals/types/readers";
import { v4 as uuidv4 } from "uuid";
import type {
  ExecuteRefundRequest,
  ExecuteRefundResponse,
  RefundPreviewRequest,
  RefundPreviewResponse,
} from "@nectarpay/api-contracts/refund";

export type RefundExecutionOutcome = "succeeded" | "pending" | "failed";

export type RefundExecutionAttempt = {
  quoteToken: string;
  idempotencyKey: string;
  request: ExecuteRefundRequest;
};

export const createRefundExecutionAttempt = (
  request: ExecuteRefundRequest,
): RefundExecutionAttempt => ({
  quoteToken: request.quoteToken,
  idempotencyKey: uuidv4(),
  request,
});

export const shouldRetainRefundExecutionAttempt = (error: unknown): boolean => {
  const candidate = error as {
    status?: number;
    data?: { code?: string };
    response?: { status?: number; data?: { code?: string } };
  };
  const response = typeof candidate?.status === "number" ? candidate : candidate?.response;
  const status = response?.status;
  if (status === 409) {
    return response?.data?.code === "REFUND_OPERATION_IN_PROGRESS";
  }
  return status === undefined || status === 408 || status === 429 || status >= 500;
};

export const resolveRefundPaymentMethodType = (payment: Payment | null): string => {
  const charge = payment?.latest_charge;
  const chargedMethodType =
    charge && typeof charge === "object" ? charge.payment_method_details?.type : undefined;
  if (chargedMethodType) return chargedMethodType;

  const method = payment?.payment_method;
  return method && typeof method === "object" ? method.type || "" : "";
};

export const resolveRefundStripeReaderId = (readers: Reader[], paymentReaderId?: string): string =>
  readers.find((reader) => reader.id === paymentReaderId)?.terminalId ||
  readers[0]?.terminalId ||
  "";

export const scopeRefundPreviewToReader = (
  request: RefundPreviewRequest,
  stripeReaderId: string,
): RefundPreviewRequest => (stripeReaderId ? { ...request, stripeReaderId } : request);

export const buildCustomRefundPreviewRequest = (
  amount: number,
  items: Extract<RefundPreviewRequest, { mode: "items" }>["items"],
): Extract<RefundPreviewRequest, { mode: "custom" }> => ({
  mode: "custom",
  amount,
  ...(Object.keys(items).length > 0 ? { items } : {}),
});

export const supportsAuthoritativeRefundQuote = (payment: Payment | null): boolean => {
  const paymentMethodType = resolveRefundPaymentMethodType(payment);
  return (
    payment?.currency?.toLowerCase() === "usd" &&
    (paymentMethodType === "card" || paymentMethodType === "card_present")
  );
};

export const getUnavailableRefundItemLabels = (quote: RefundPreviewResponse): string[] => {
  const unavailableCartKeys = new Set(
    quote.issues
      .filter((issue) => ["TAX_DATA_UNAVAILABLE", "ITEM_VALUE_UNAVAILABLE"].includes(issue.code))
      .flatMap((issue) => (issue.cartKey ? [issue.cartKey] : [])),
  );
  return [...unavailableCartKeys].map((cartKey) => {
    const line = quote.lines.find((candidate) => candidate.cartKey === cartKey);
    return line?.productName || line?.priceName || cartKey;
  });
};

export const classifyRefundExecutionStatus = (
  status: ExecuteRefundResponse["status"],
): RefundExecutionOutcome => {
  if (status === "succeeded") return "succeeded";
  if (status === "pending" || status === "requires_action") return "pending";
  return "failed";
};
